/**
 * Admin Permissions System Constants
 *
 * Route-based permission system: the router is the single source of truth.
 * Route names ARE the permission keys. This file defines action permissions,
 * group ordering, migration utilities, and default groups.
 */

// System role constants
export const SYSTEM_ROLES = {
  SUPERADMIN: 'super',
  ADMIN: 'admin',
  USER: 'user'
};

// System role labels
export const SYSTEM_ROLE_LABELS = {
  [SYSTEM_ROLES.SUPERADMIN]: 'Super Admin',
  [SYSTEM_ROLES.ADMIN]: 'Admin',
  [SYSTEM_ROLES.USER]: 'User'
};

/**
 * Action permissions — non-route capabilities checked in components.
 * These are NOT route names; they represent abilities like impersonation or deletion.
 */
export const ACTION_PERMISSIONS = [
  { key: 'impersonation', label: 'Client Impersonation', description: 'Ability to impersonate client company views' },
  { key: 'projects.delete', label: 'Delete Projects', description: 'Permanently delete projects and associated data' },
  { key: 'mywork.work-pool', label: 'Work Pool Tab', description: 'Access to the Work Pool tab' },
  { key: 'mywork.switch-users', label: 'Switch Users', description: 'Ability to view My Work as other users' },
  { key: 'profile.view-users', label: 'View User Profiles', description: 'View other users\' profile and notification settings' },
  { key: 'deliverables.skip-internal-review', label: 'Skip Internal Review', description: 'Bypass the internal review phase and send a deliverable straight to client review' },
  { key: 'timeoff.manage', label: 'Manage Time Off', description: 'Eligible to be a time-off approver — approve, reject, and revoke requests' }
];

/** Drawer group ordering for the PermissionDrawer UI */
export const PERMISSION_GROUP_ORDER = [
  'Projects',
  'Podcasts',
  'Calendar',
  'Companies',
  'Conversations',
  'Reports',
  'Tools',
  'Configuration'
];

/**
 * Migration map: old abstract permission keys → new route-name keys.
 * Used by migratePermissions() to transparently convert legacy Firestore data.
 */
const OLD_TO_NEW_PERMISSION_MAP = {
  'projects': ['admin-projects', 'admin-deliverables'],
  'calendar': ['admin-calendar'],
  // The company page's sections each carry their own key now — the old single
  // 'admin-companies' key (and the legacy 'companies' key) expand to all of them.
  'companies': [
    'admin-company-contracts', 'admin-company-storage',
    'admin-company-users', 'admin-company-agency',
    'admin-company-activity', 'admin-company-settings'
  ],
  'admin-companies': [
    'admin-company-contracts', 'admin-company-storage',
    'admin-company-users', 'admin-company-agency',
    'admin-company-activity', 'admin-company-settings'
  ],
  'tools': ['admin-asset-upload'],
  'tools.asset-upload': ['admin-asset-upload'],
  'reports': [
    'admin-feedback', 'admin-asset-audit', 'admin-user-access-report',
    'admin-loa-report', 'admin-dgf-report',
    'admin-editor-stage-duration'
  ],
  'reports.feedback': ['admin-feedback'],
  'reports.asset-audit': ['admin-asset-audit'],
  'reports.user-access': ['admin-user-access-report'],
  'reports.loa': ['admin-loa-report'],
  'config': [
    'admin-users', 'admin-form-builder', 'admin-pricing-config',
    'admin-automation-rules', 'admin-workpool-settings', 'admin-dgf-config',
    'admin-scheduling-rules'
  ],
  'config.admin-management': ['admin-users'],
  'config.intake': ['admin-form-builder'],
  'config.automation': ['admin-automation-rules'],
  // Support Chat was replaced by Conversations.
  'admin-support': ['admin-conversations'],
  // Action keys pass through unchanged
  'impersonation': ['impersonation'],
  'mywork': [],
  'projects.delete': ['projects.delete'],
  'mywork.work-pool': ['mywork.work-pool'],
  'mywork.switch-users': ['mywork.switch-users']
};

/**
 * Migrate an array of old-format permissions to new route-name format.
 * If the array already contains route-name keys (starts with 'admin-'),
 * they pass through unchanged. Old abstract keys are expanded.
 *
 * @param {string[]} oldPerms - Permission array (may be old or new format)
 * @returns {string[]} - Deduplicated array of new-format permission keys
 */
export function migratePermissions(oldPerms) {
  if (!oldPerms || oldPerms.length === 0) return [];

  const result = new Set();

  for (const perm of oldPerms) {
    const mapped = OLD_TO_NEW_PERMISSION_MAP[perm];
    if (mapped) {
      // Old-format key — expand to new route names
      mapped.forEach(p => result.add(p));
    } else {
      // Already new format or unknown — pass through
      result.add(perm);
    }
  }

  return Array.from(result);
}

/**
 * Get all permission keys (route names + action keys) by scanning the router.
 * Used to give super admins the full set.
 *
 * @param {import('vue-router').Router} router - Vue Router instance
 * @returns {string[]}
 */
export function getAllPermissionKeys(router) {
  const keys = new Set();

  // Collect all route names that have a permissionGroup and are NOT child routes
  for (const route of router.getRoutes()) {
    if (route.meta?.permissionGroup && route.name && !route.meta?.parentRoute) {
      keys.add(route.name);
    }
  }

  // Add all action permission keys
  for (const action of ACTION_PERMISSIONS) {
    keys.add(action.key);
  }

  return Array.from(keys);
}

// Default permission groups (seeded to Firestore if empty)
export const DEFAULT_PERMISSION_GROUPS = [
  {
    id: 'full-access',
    name: 'Full Access',
    description: 'Access to all admin features',
    permissions: [
      // Route permissions
      'admin-projects', 'admin-deliverables', 'admin-calendar',
      'admin-company-contracts', 'admin-company-storage',
      'admin-company-users', 'admin-company-agency',
      'admin-company-activity', 'admin-company-settings',
      'admin-asset-upload',
      'admin-feedback', 'admin-asset-audit', 'admin-user-access-report',
      'admin-loa-report', 'admin-dgf-report',
      'admin-editor-stage-duration', 'admin-msa-invoicing',
      'admin-podcast-publishing', 'admin-delivery-risk', 'admin-email-delivery',
      'admin-users', 'admin-form-builder', 'admin-pricing-config',
      'admin-automation-rules', 'admin-workpool-settings', 'admin-dgf-config',
      'admin-scheduling-rules',
      'admin-podcasts', 'admin-conversations',
      // Action permissions
      'impersonation', 'projects.delete', 'mywork.work-pool', 'mywork.switch-users', 'timeoff.manage'
    ],
    isSystem: true,
    order: 0
  },
  {
    id: 'project-manager',
    name: 'Project Manager',
    description: 'Access to projects, deliverables, and calendar',
    permissions: [
      'admin-projects', 'admin-deliverables', 'admin-calendar', 'impersonation'
    ],
    isSystem: true,
    order: 1
  },
  {
    id: 'company-manager',
    name: 'Company Manager',
    description: 'Access to companies and reports',
    permissions: [
      'admin-company-contracts', 'admin-company-storage',
      'admin-company-users', 'admin-company-agency',
      'admin-company-activity', 'admin-company-settings',
      'admin-feedback', 'admin-asset-audit', 'admin-user-access-report',
      'admin-loa-report', 'admin-dgf-report',
      'admin-editor-stage-duration',
      'impersonation'
    ],
    isSystem: true,
    order: 2
  }
];
